/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState } from 'react';
import { usePublishersStore } from '../../store/models';
import { useErrorStore } from '../../store/models/error';
import useTriggeredErrorDialog from '../../store/models/error/useTriggeredErrorDialog';
import { serviceErrorSerializer } from '../../store/models/publishers/errors/serviceErrors';

function useEcalWorldFeaturedSwitch (props) {
    const { row } = props;
    const publishersStore = usePublishersStore();
    const errorStore = useErrorStore();

    const [confirmOpen, setConfirmOpen] = useState(false);

    // Only this row's toggle interaction should own the error dialog — updateFeatured
    // is shared across every row's switch, so we guard on our own trigger flag.
    const { renderErrorDialog, markTriggered, clearTriggered } = useTriggeredErrorDialog({
        hook: 'useEcalWorldFeaturedSwitch',
        serviceErrorSerializer,
        errorObservables: [
            errorStore.observed.errors.updateFeatured,
        ],
    });

    // Prefer a locally tracked override (set right after a successful toggle) over
    // the row's own fetched value, so only this switch reacts — not the whole list.
    const isFeatured = useMemo(() => {
        const override = publishersStore.featuredOverrides[row.publisherId];
        if (override !== undefined) {
            return override;
        }
        return row.featured === true;
    }, [publishersStore.observed.featuredOverrides, row.publisherId, row.featured]);

    const tooltipTitle = isFeatured
        ? 'Featured in ECAL World — click to unfeature'
        : 'Feature in ECAL World';

    const dialogTitle = isFeatured ? 'Unfeature Publisher' : 'Feature Publisher';

    const dialogText = isFeatured
        ? 'Are you sure you want to remove this publisher from ECAL World featured?'
        : 'Are you sure you want to feature this publisher in ECAL World?';

    function handleToggle (e) {
        e.stopPropagation();
        e.preventDefault();
        setConfirmOpen(true);
    }

    function handleConfirm () {
        markTriggered();
        // Closes immediately (matches the pre-existing fire-and-forget behavior of this
        // dialog) — clearTriggered only runs once the call actually settles, which per
        // this codebase's API layer only happens on success, so a failure leaves the
        // guard armed for exactly this row's error and nothing else.
        publishersStore.updateFeatured({
            publisherId: row.publisherId,
            featured: !isFeatured,
        }).then(() => {
            clearTriggered();
        });
        setConfirmOpen(false);
    }

    function handleCancel () {
        setConfirmOpen(false);
    }

    return {
        isFeatured,
        confirmOpen,
        tooltipTitle,
        dialogTitle,
        dialogText,
        handleToggle,
        handleConfirm,
        handleCancel,
        renderErrorDialog,
    };
}

export default useEcalWorldFeaturedSwitch;
